import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Badge,
  Drawer,
  AppBar,
  Toolbar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';

interface AlertItem {
  id: string;
  type: 'breach' | 'warning' | 'info' | 'success';
  severity: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  fundName: string;
  indicator: string;
  value: number;
  threshold: number;
  timestamp: string;
  acknowledged: boolean;
}

interface AlertSystemProps {
  open: boolean;
  onClose: () => void;
}

const AlertSystem: React.FC<AlertSystemProps> = ({ open, onClose }) => {
  const { getAccessToken } = useAuth();
  
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds

  useEffect(() => {
    if (open) {
      loadAlerts();
    }
  }, [open]);

  useEffect(() => {
    let interval: number;
    if (autoRefresh && open) {
      interval = setInterval(loadAlerts, refreshInterval * 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, refreshInterval, open]);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      // Try to load alerts from API endpoint
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/alerts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts(Array.isArray(data) ? data : []);
      } else {
        // STRICT: No sample data - only real database data
        setAlerts([]);
      }
    } catch (err) {
      console.error('Failed to load alerts:', err);
      // STRICT: No sample data - only real database data
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId: string) => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      // In a real implementation, this would update the alert status via API
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId ? { ...alert, acknowledged: true } : alert
      ));
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const getAlertIcon = (type: string, severity: string) => {
    switch (type) {
      case 'breach':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'unacknowledged') return !alert.acknowledged;
    return alert.type === filter;
  });

  const unacknowledgedCount = alerts.filter(alert => !alert.acknowledged).length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 400,
          maxWidth: '90vw'
        }
      }}
    >
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Badge badgeContent={unacknowledgedCount} color="error">
            <NotificationsIcon />
          </Badge>
          <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
            Alerts & Notifications
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {/* Controls */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              Alert Settings
            </Typography>
            <IconButton onClick={loadAlerts} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
          
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              label="Filter"
            >
              <MenuItem value="all">All Alerts</MenuItem>
              <MenuItem value="unacknowledged">Unacknowledged</MenuItem>
              <MenuItem value="breach">Breaches</MenuItem>
              <MenuItem value="warning">Warnings</MenuItem>
              <MenuItem value="info">Information</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label="Auto Refresh"
          />

          {autoRefresh && (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel>Refresh Interval</InputLabel>
              <Select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(e.target.value as number)}
                label="Refresh Interval"
              >
                <MenuItem value={10}>10 seconds</MenuItem>
                <MenuItem value={30}>30 seconds</MenuItem>
                <MenuItem value={60}>1 minute</MenuItem>
                <MenuItem value={300}>5 minutes</MenuItem>
              </Select>
            </FormControl>
          )}
        </Paper>

        {/* Alerts List */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Recent Alerts ({filteredAlerts.length})
        </Typography>

        {filteredAlerts.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No alerts to display
            </Typography>
          </Paper>
        ) : (
          <List>
            {filteredAlerts.map((alert, index) => (
              <React.Fragment key={alert.id}>
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    bgcolor: alert.acknowledged ? 'transparent' : 'action.hover',
                    borderRadius: 1,
                    mb: 1,
                    opacity: alert.acknowledged ? 0.7 : 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getAlertIcon(alert.type, alert.severity)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            {alert.title}
                          </Typography>
                          <Chip
                            label={alert.severity}
                            size="small"
                            color={getSeverityColor(alert.severity) as any}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {alert.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {alert.fundName} • {formatTimestamp(alert.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {alert.type === 'breach' && (
                    <Box sx={{ ml: 5, mb: 1 }}>
                      <Typography variant="body2">
                        <strong>{alert.indicator}:</strong> {alert.value} 
                        (Threshold: {alert.threshold})
                      </Typography>
                      <Typography variant="body2" color="error">
                        Breach: {((alert.value - alert.threshold) / alert.threshold * 100).toFixed(1)}% over threshold
                      </Typography>
                    </Box>
                  )}

                  {!alert.acknowledged && (
                    <Box sx={{ ml: 5 }}>
                      <Button
                        size="small"
                        onClick={() => acknowledgeAlert(alert.id)}
                        variant="outlined"
                      >
                        Acknowledge
                      </Button>
                    </Box>
                  )}
                </ListItem>
                {index < filteredAlerts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default AlertSystem;
